import ClientContractApiClient from '@/api/ClientContractApiClient';
import MissionPlaceApiClient from '@/api/MissionPlaceApiClient';
import OrderApiClient from '@/api/OrderApiClient';
import TimeTicketApiClient from '@/api/TimeTicketApiClient';
import { i18n } from '@/i18n';
import AjaxCooler from '@/utils/AjaxCooler';
import { Bus, useAside, useModal, useNotif } from '@orion.ui/orion';
import { defineAsyncComponent } from 'vue';
import useSelfService from './SelfService';

class MissionPlaceClientService implements ResourceService {

	private cooler = new AjaxCooler(this.api.list);

	private get api () { return MissionPlaceApiClient }

	async storeAsync (payload: IAddMissionPlaceModel) {
		return new Promise<void>(async (resolve) => {
			try {
				await this.api.create(payload);
				Bus.emit(`MissionPlacePagedListViewSetup:refreshList`, true);
				resolve();
			}
			catch (e: any) {
				useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
			}
		});
	}

	async getAsync (id: number) {
		const { data } = await this.api.read(id);
		return data;
	}

	async updateAsync (id: number, payload: IUpdateMissionPlaceModel) {
		try {
			await this.api.update(id, payload);
			Bus.emit(`MissionPlaceCardSetup:${id}:update`, payload);
			Bus.emit(`MissionPlaceAsideDetailsSetup:${id}:update`, payload);
		}
		catch (e: any) {
			useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
		}
	}

	async deleteAsync (id: number) {
		try {
			await this.api.delete(id);
			Bus.emit(`MissionPlacePagedListViewSetup:refreshList`);
		}
		catch (e: any) {
			useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
		}
	}

	async listAsync (forceRefresh = false) {
		return await this.cooler.coolAsync(forceRefresh);
	}

	async promptMissionPlaceModalAsync (id?: number) {
		useModal({
			Nested: defineAsyncComponent(() => import('@/components/mission-place/MissionPlaceModalPrompt.vue')),
			NestedProps: { id },
			size: 'sm',
		});
	}

	async showMissionPlaceAsideDetailsAsync (id: number) {
		useAside({
			Nested: defineAsyncComponent(() => import('@/components/mission-place/MissionPlaceAsideDetails.vue')),
			NestedProps: { id },
		});
	}

	async countLinkedOrdersAsync (...args: Parameters<typeof OrderApiClient.countByMissionPlaceId>) {
		if (!useSelfService().self.isClient()) return;
		const { data } = await OrderApiClient.countByMissionPlaceId(...args);
		return data;
	}

	async countLinkedTimeTicketsAsync (...args: Parameters<typeof TimeTicketApiClient.countByMissionPlaceId>) {
		if (!useSelfService().self.isClient()) return;
		const { data } = await TimeTicketApiClient.countByMissionPlaceId(...args);
		return data;
	}

	async countLinkedClientContractsAsync (...args: Parameters<typeof ClientContractApiClient.countByMissionPlaceId>) {
		if (!useSelfService().self.isClient()) return;
		const { data } = await ClientContractApiClient.countByMissionPlaceId(...args);
		return data;
	}

	async readAsync (...args: Parameters<typeof MissionPlaceApiClient.read>) {
		const { data } = await MissionPlaceApiClient.read(...args);
		return data;
	}

}

const missionPlaceClientServiceSingleton = new MissionPlaceClientService();

export function useMissionPlaceClientService () {
	return missionPlaceClientServiceSingleton;
}
